import { resolveMinorUnits } from "./resolveMinorUnits";

// Every localised payload carries at least this; the money fields it overrides
// vary per endpoint, so the caller names them.
type TLocalizedAmount = {
  currency: string;
  symbol: string;
  rate: number;
};

type TLocalizable = {
  currency?: string;
  localized_amount?: TLocalizedAmount;
};

/**
 * Swaps the localised amounts over the USD ones whenever the payload carries
 * them — the server, not the client, decides whether a market is localised.
 *
 * @param label - names the payload in the swap log, e.g. "checkout summary".
 * @param pickAmounts - the localised money fields to overlay, read only once
 *   the currency is known to be displayable.
 * @param gateSaysLocalised - what `useLocalisedPricing` inferred client-side,
 *   checked only so a disagreement with the payload is visible in the logs.
 */
export function applyLocalizedAmount<T extends TLocalizable>(
  data: T,
  label: string,
  pickAmounts: (localized: NonNullable<T["localized_amount"]>) => Partial<T>,
  gateSaysLocalised?: boolean,
): T {
  const localized = data.localized_amount;

  if (gateSaysLocalised !== undefined && gateSaysLocalised !== !!localized) {
    console.error(
      `[LOCALISED-PRICING] gate/payload mismatch on ${label}: client gate says ${gateSaysLocalised}, localized_amount is ${localized ? localized.currency : "absent"} — following the payload`,
    );
  }

  if (!localized) return data;

  // Without a confirmed minor-unit entry every amount would display 100x off.
  if (!resolveMinorUnits(localized.currency)) {
    console.error(
      `[LOCALISED-PRICING] no minor-unit entry for ${localized.currency}, keeping USD amounts`,
    );
    return data;
  }

  console.log(`[LOCALISED-PRICING]: swapped ${label}`, {
    currency: localized.currency,
    symbol: localized.symbol,
    rate: localized.rate,
  });

  return {
    ...data,
    currency: localized.currency,
    ...pickAmounts(localized as NonNullable<T["localized_amount"]>),
  };
}
